import { Empty, Spin, message } from 'antd'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import {
  deleteWorkIdea,
  getTeamWorkIdeaList,
  getWorkIdeaList,
  suggestWorkIdeaToTeam,
} from '../../../api/aposWorkIdea.api'
import AopsCommonModalLayout from '../../../components/apos/modal/AopsCommonModalLayout'
import { menuSelectedTeam } from '../../../store/aops'
import { userState } from '../../../store/user'
import WorkIdeaCard from './ui/WorkIdeaCard'
import WorkIdeaFormModal from './WorkIdeaFormModal'
import './WorkIdeaListModal.scss'

import { userAuthStatus } from '../../../store/user'

const WorkIdeaListModal = ({
  open,
  onClose,
  salusUserId,
  teamName,
  mode = 'personal', // 'personal' or 'team'
  onEditClick,
}) => {
  const [workIdeasData, setWorkIdeasData] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })
  const [user] = useRecoilState(userState)
  const [loading, setLoading] = useState(false)
  const loadingRef = useRef(false)
  const [selectedTeamByMenu] = useRecoilState(menuSelectedTeam)
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [selectedWorkIdea, setSelectedWorkIdea] = useState(null)
  const [userAuth] = useRecoilState(userAuthStatus)

  const fetchPersonalWorkIdeas = useCallback(
    async (page = 0, append = false) => {
      if (loadingRef.current) return

      loadingRef.current = true
      setLoading(true)
      try {
        const res = await getWorkIdeaList({
          userId: salusUserId,
          page,
          size: 20,
          sort: 'createdAt,desc',
        })
        console.log('fetchPersonalWorkIdeas res = ', res)
        setWorkIdeasData((prev) => ({
          content: append
            ? [...prev.content, ...(res?.data?.content || [])]
            : res?.data?.content || [],
          pageable: res?.data?.pageable || { pageNumber: 0, pageSize: 10 },
          totalElements: res?.data?.totalElements || 0,
        }))
      } catch (error) {
        console.error('생각보관함 조회 실패:', error)
        if (!append) {
          setWorkIdeasData({
            content: [],
            pageable: { pageNumber: 0, pageSize: 10 },
            totalElements: 0,
          })
        }
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [salusUserId],
  )

  const fetchTeamWorkIdeas = useCallback(
    async (teamName, page = 0, append = false) => {
      if (loadingRef.current) return

      loadingRef.current = true
      setLoading(true)
      try {
        // console.log('🦶selectedTeamByMenuKo::', teamName)
        const res = await getTeamWorkIdeaList({
          teamName,
          userId: user?.id,
          page,
          size: 20,
          sort: 'createdAt,desc',
        })
        console.log('🍬팀! fetchTeamWorkIdeas res = ', res)
        setWorkIdeasData((prev) => ({
          content: append
            ? [...prev.content, ...(res?.data?.content || [])]
            : res?.data?.content || [],
          pageable: res?.data?.pageable || { pageNumber: 0, pageSize: 10 },
          totalElements: res?.data?.totalElements || 0,
        }))
      } catch (error) {
        console.error('팀 생각보관함 조회 실패:', error)
        if (!append) {
          setWorkIdeasData({
            content: [],
            pageable: { pageNumber: 0, pageSize: 10 },
            totalElements: 0,
          })
        }
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [user?.id],
  )

  const refetchWorkIdeas = useCallback(async () => {
    if (mode === 'personal') {
      await fetchPersonalWorkIdeas(0, false)
    } else if (mode === 'team' && selectedTeamByMenu) {
      // console.log('🦶selectedTeamByMenu::', selectedTeamByMenu)
      await fetchTeamWorkIdeas(selectedTeamByMenu, 0, false)
    }
  }, [mode, fetchPersonalWorkIdeas, fetchTeamWorkIdeas, selectedTeamByMenu])

  const hasMore = workIdeasData.content.length < workIdeasData.totalElements
  const loadMoreTimeoutRef = useRef(null)

  const loadMoreWorkIdeas = useCallback(() => {
    if (loadingRef.current) return

    const currentHasMore =
      workIdeasData.content.length < workIdeasData.totalElements
    if (!currentHasMore) return

    const currentPage = workIdeasData.pageable?.pageNumber || 0

    if (mode === 'personal') {
      fetchPersonalWorkIdeas(currentPage + 1, true)
    } else if (mode === 'team' && selectedTeamByMenu) {
      fetchTeamWorkIdeas(selectedTeamByMenu, currentPage + 1, true)
    }
  }, [
    mode,
    selectedTeamByMenu,
    fetchPersonalWorkIdeas,
    fetchTeamWorkIdeas,
    workIdeasData.content.length,
    workIdeasData.totalElements,
    workIdeasData.pageable?.pageNumber,
  ])

  useEffect(() => {
    if (open) {
      if (mode === 'personal') {
        fetchPersonalWorkIdeas(0, false)
      } else if (mode === 'team' && selectedTeamByMenu) {
        fetchTeamWorkIdeas(selectedTeamByMenu, 0, false)
      }
    }
  }, [
    open,
    mode,
    fetchPersonalWorkIdeas,
    fetchTeamWorkIdeas,
    selectedTeamByMenu,
  ])

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current)
      }
    }
  }, [])

  const handleDelete = async (ideaId) => {
    const result = await Swal.fire({
      customClass: { popup: 'custom-alert' },
      title: '생각노트 삭제',
      text: '정말 이 생각노트를 삭제하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    })

    if (result.isConfirmed) {
      try {
        const res = await deleteWorkIdea(ideaId, user?.id)
        // console.log('handleDelete res = ', res)
        if (res.status === 200) {
          message.success('생각노트가 삭제되었습니다.')
          // 삭제 성공 후 리패치
          await refetchWorkIdeas()
        } else {
          message.error('생각노트 삭제에 실패했습니다.')
        }
      } catch (error) {
        console.error('생각노트 삭제 실패:', error)
        message.error('생각노트 삭제에 실패했습니다.')
      }
    }
  }

  const handleSuggestToTeam = async (item) => {
    const result = await Swal.fire({
      customClass: { popup: 'custom-alert' },
      title: '팀에게 아이디어 제안',
      text: '정말 제안하시겠습니까?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: '제안',
      cancelButtonText: '취소',
    })

    if (result.isConfirmed) {
      try {
        const res = await suggestWorkIdeaToTeam(item.id)
        // console.log('handleSuggestToTeam res = ', res)
        if (res.status === 200) {
          message.success('팀에게 아이디어가 제안되었습니다.')
          // 제안 성공 후 리패치
          await refetchWorkIdeas()
        } else {
          message.error('팀에게 아이디어 제안에 실패했습니다.')
        }
      } catch (error) {
        console.error('팀에게 아이디어 제안 실패:', error)
        message.error('팀에게 아이디어 제안에 실패했습니다.')
      }
    }
  }

  const handleEditClick = (item) => {
    if (onEditClick) {
      // 외부에서 모달을 관리하는 경우
      onEditClick(item)
    } else {
      // 내부에서 모달을 관리하는 경우
      setSelectedWorkIdea(item)
      setIsFormModalOpen(true)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return dayjs(dateString).format('YY.MM.DD')
  }

  // 스크롤 핸들러
  const handleScroll = useCallback(
    (e) => {
      if (loadingRef.current) return

      const target = e.currentTarget
      if (!target) return

      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current)
      }

      loadMoreTimeoutRef.current = setTimeout(() => {
        const { scrollTop, clientHeight, scrollHeight } = target
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

        if (scrollPercentage > 0.8 && hasMore) {
          loadMoreWorkIdeas()
        }
      }, 200)
    },
    [hasMore, loadMoreWorkIdeas],
  )
  // 모달 타이틀 렌더링
  const renderModalTitle = () => {
    const titleName =
      mode === 'team'
        ? `${userAuth.teamNameKo}팀` || ''
        : workIdeasData.content.length > 0
          ? workIdeasData.content[0]?.writerName
          : user.name

    return (
      <div
        className="modal-title"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <span className="title-text">생각보관함</span>
        <span
          className="title-divider"
          style={{
            width: '1px',
            height: '16px',
            background: '#E2EAEF',
            margin: '0 10px',
            display: 'inline-block',
          }}
        />
        <span
          className="title-name"
          style={{
            fontSize: '16px',
            color: '#6D747A',
            fontWeight: 600,
            letterSpacing: '-0.4px',
            lineHeight: '1.5',
          }}
        >
          {titleName}
        </span>
      </div>
    )
  }

  return (
    <>
      <AopsCommonModalLayout
        open={open}
        onClose={onClose}
        title={renderModalTitle()}
      >
        <div className="form-container work-idea-list-modal">
          <div className="form-wrapper">
            <div
              className={`content-wrapper ${mode === 'team' ? 'team-mode' : ''}`}
              onScroll={handleScroll}
              style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
            >
              {loading && workIdeasData.content.length === 0 ? (
                <div className="loading-wrapper">
                  <Spin size="small" />
                </div>
              ) : workIdeasData.content.length === 0 ? (
                <Empty
                  description="생각노트가 없습니다"
                  className="empty-wrapper"
                />
              ) : (
                <>
                  <div className="work-idea-grid">
                    {workIdeasData.content.map((item) => (
                      <WorkIdeaCard
                        key={item.id}
                        item={item}
                        mode={mode}
                        onEditClick={handleEditClick}
                        onDelete={handleDelete}
                        onSuggestToTeam={handleSuggestToTeam}
                        formatDate={formatDate}
                        colorCode={item.colorCode}
                        emoji={item.emoji}
                      />
                    ))}
                  </div>
                  {/* 무한 스크롤 로딩 */}
                  {loading && workIdeasData.content.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '20px',
                      }}
                    >
                      <Spin size="small" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {mode === 'personal' && (
          <div
            // className="popup-footer"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingRight: '20px',
            }}
          >
            <button
              type="button"
              className="work-idea-create-button"
              onClick={() => setIsFormModalOpen(true)}
            >
              생각노트 적어보기
            </button>
          </div>
        )}
      </AopsCommonModalLayout>

      {(!onEditClick || mode === 'personal') && (
        <WorkIdeaFormModal
          open={isFormModalOpen}
          onClose={() => {
            setIsFormModalOpen(false)
            setSelectedWorkIdea(null)
          }}
          initialData={selectedWorkIdea}
          onSuccess={refetchWorkIdeas}
        />
      )}
    </>
  )
}

export default WorkIdeaListModal
